/**
 * Centralized SEO configuration for Layerre
 *
 * This file contains per-route metadata and helpers for managing
 * SEO tags via react-helmet-async. All route-specific titles,
 * descriptions, and Open Graph/Twitter Card metadata are defined here.
 */

export interface SeoMetadata {
  title: string;
  description: string;
  image: string;
  url: string;
  type: 'website' | 'article' | 'video.other';
  siteName?: string;
}

/**
 * Default fallback metadata
 */
export const DEFAULT_SEO: SeoMetadata = {
  title: 'Layerre - The API for automating designs',
  description:
    "Bulk-generate Canva content at scale with Layerre's API. Automate design variations, turn Canva designs into templates, and scale content production. Get started!",
  image: 'https://layerre.com/link-card.png',
  url: 'https://layerre.com',
  type: 'website',
  siteName: 'Layerre',
};

/**
 * Route-to-metadata mapping
 *
 * Add new routes here to ensure consistent SEO across the site
 */
export const ROUTE_SEO_MAP: Record<string, SeoMetadata> = {
  '/': DEFAULT_SEO,
  '/docs': {
    title: 'API Documentation | Layerre',
    description:
      'Complete API documentation for Layerre. Learn how to automate Canva designs, create templates, and generate design variations programmatically.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/docs/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/docs/mcp': {
    title: 'Layerre MCP Server: Connect Claude, Cursor & VS Code | Layerre',
    description:
      'Connect Claude, Cursor, VS Code and other AI assistants to Layerre. Import Canva designs as templates and generate images and PDFs with the Layerre MCP server.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/docs/mcp/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/pricing': {
    title: 'Pricing | Layerre',
    description:
      'Pricing plans for Layerre. Scale design automation with pay-as-you-go or monthly plans. Free tier available.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/pricing/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/canva-palette': {
    title: 'Canva Palette Extractor - Free Color & Font Analyzer | Layerre',
    description:
      'Extract colors, fonts, and images from any Canva design in seconds. Free tool to analyze your Canva designs and get complete color palettes, font lists, and image resources.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/canva-palette/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/terms': {
    title: 'Terms and Conditions | Layerre',
    description:
      "Terms and conditions for using Layerre's design automation API and services.",
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/terms/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/privacy': {
    title: 'Privacy Policy | Layerre',
    description:
      'Privacy policy for Layerre (PIPEDA, GDPR / DSGVO). How we collect, use, and protect your data, including subprocessors and cookies.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/privacy/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/dpa': {
    title: 'Data Processing Addendum | Layerre',
    description:
      'Layerre Data Processing Addendum (DPA / AVV) under GDPR Article 28 for customers who process personal data through our design automation API.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/dpa/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/limitations': {
    title: 'Limitations | Layerre',
    description:
      "Learn about the current limitations and supported features of Layerre's Canva design automation API.",
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/limitations/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/blog': {
    title: 'Blog | Layerre',
    description:
      'Latest insights, tips, and guides on design automation and Canva workflows',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/blog/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/whats-new': {
    title: "What's New | Layerre",
    description:
      'Product releases, feature launches, and improvements shipping in Layerre.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/whats-new/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/whats-new/google-sheets-import': {
    title: 'Create variations from Google Sheets | Layerre',
    description:
      "Introducing Layerre's native Google Sheets integration. Import data from Google Sheets and generate hundreds of variations of your Canva template.",
    image: 'https://layerre.com/blogs/google-sheets-import/hero.png',
    url: 'https://layerre.com/whats-new/google-sheets-import/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/whats-new/dashboard-search-and-tags': {
    title: 'Search and tags for your designs | Layerre',
    description:
      'Find designs by name in the dashboard, organize them with tags, and filter or search the same way via the API.',
    image:
      'https://layerre.com/blogs/dashboard-search-and-tags/dashboard-search.png',
    url: 'https://layerre.com/whats-new/dashboard-search-and-tags/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/whats-new/combined-pdf-exports': {
    title: 'Bulk and combined PDF exports | Layerre',
    description:
      'Generate many variants in one request and merge PDF outputs into a single multi-page file in the app or via API.',
    image: 'https://layerre.com/blogs/combined-pdf-exports/bulk-variations.png',
    url: 'https://layerre.com/whats-new/combined-pdf-exports/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/whats-new/multi-page-canva-imports': {
    title: 'Multi-page Canva imports | Layerre',
    description:
      'Import multiple pages from a single Canva design on Plus and Pro plans. Up to 25 pages on Plus, 200 on Pro.',
    image:
      'https://layerre.com/blogs/multi-page-canva-imports/stacked-pages.png',
    url: 'https://layerre.com/whats-new/multi-page-canva-imports/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/whats-new/table-and-sheet-support': {
    title: 'Tables and Sheets Are Now Supported | Layerre',
    description:
      "Layerre now supports Canva's native table and sheet layer types, so you can control individual cells when generating variants without flattening layouts.",
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/whats-new/table-and-sheet-support/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/whats-new/more-control-over-exports-and-images': {
    title: 'More Control Over Exports and Images | Layerre',
    description:
      'Export as PNG, PDF, WebP, or JPEG, and control how swapped images sit in frames with Fit and Fill (including via API).',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/whats-new/more-control-over-exports-and-images/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/whats-new/profile-settings-and-autosave': {
    title: 'Profile settings and autosave | Layerre',
    description:
      'Manage your account, connected sign-in methods, and password from a dedicated profile page. Template editor changes now save automatically.',
    image: 'https://layerre.com/blogs/profile-settings-and-autosave/1.png',
    url: 'https://layerre.com/whats-new/profile-settings-and-autosave/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/integrations': {
    title: 'Integrations | Layerre',
    description:
      'Connect Make, Zapier, and n8n to Layerre, import Google Sheets in Generate variations, or wire Airtable, Notion, and other apps through automation.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/integrations/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/use-cases': {
    title: 'Use Cases | Layerre',
    description: 'Real-world examples and use cases for design automation',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/use-cases/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/tutorials': {
    title: 'Video Tutorials | Layerre',
    description:
      'Step-by-step video guides to help you automate design generation with Layerre. From basic dashboard usage to advanced n8n workflows.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/tutorials/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/tutorials/social-media-automation': {
    title:
      'How to Automate Your Social Media Content with Layerre | Video Tutorial',
    description:
      'Learn how to create and automate social media content using Layerre.com - no design experience needed. Create social media post variants in minutes.',
    image: 'https://img.youtube.com/vi/m2szKpIgid8/maxresdefault.jpg',
    url: 'https://layerre.com/tutorials/social-media-automation/',
    type: 'video.other',
    siteName: 'Layerre',
  },
  '/tutorials/webhook-to-layerre-to-slack': {
    title: 'n8n Tutorial 1: Webhook-Triggered Image Generation with Layerre',
    description:
      'Trigger Layerre image generation from a webhook using n8n. No spreadsheet required. Return results to the webhook and optionally post to Slack.',
    image: 'https://img.youtube.com/vi/LMUNMfAiXSE/maxresdefault.jpg',
    url: 'https://layerre.com/tutorials/webhook-to-layerre-to-slack/',
    type: 'video.other',
    siteName: 'Layerre',
  },
  '/tutorials/google-sheets-to-layerre': {
    title:
      'n8n Tutorial 2: Create Bulk Image Variants from Google Sheets with Layerre',
    description:
      'Create image variants from your Google Sheets data using n8n and Layerre. Connect spreadsheet data to Layerre and generate multiple image variants in minutes.',
    image: 'https://img.youtube.com/vi/_ZLGdP3JzKg/maxresdefault.jpg',
    url: 'https://layerre.com/tutorials/google-sheets-to-layerre/',
    type: 'video.other',
    siteName: 'Layerre',
  },
  '/tutorials/airtable-to-layerre': {
    title:
      'n8n Tutorial 3: Turn Airtable Records into Image Variants Using Layerre',
    description:
      'Connect Airtable with Layerre using n8n to generate image variants from your records. Create a template from Canva, pull Airtable data, and write results back.',
    image: 'https://img.youtube.com/vi/wM5KELaPQ40/maxresdefault.jpg',
    url: 'https://layerre.com/tutorials/airtable-to-layerre/',
    type: 'video.other',
    siteName: 'Layerre',
  },
  '/tutorials/make-google-sheets-to-layerre-to-drive': {
    title:
      'Make Tutorial 1: Create Bulk Image Variants from Google Sheets and Save to Google Drive',
    description:
      'Use Make to pull rows from Google Sheets, generate image variants from your Canva design using Layerre, and automatically save results to Google Drive.',
    image: 'https://img.youtube.com/vi/m9igU7EJEt8/maxresdefault.jpg',
    url: 'https://layerre.com/tutorials/make-google-sheets-to-layerre-to-drive/',
    type: 'video.other',
    siteName: 'Layerre',
  },
  '/tutorials/make-openai-to-layerre-images': {
    title:
      'Make Tutorial 2: Let AI Write Your Content and Layerre Turn It Into Images',
    description:
      'Use OpenAI to generate multiple text variations, then apply each to your Canva design via Layerre, producing a batch of ready-to-use images for social media, ads, or A/B testing.',
    image: 'https://img.youtube.com/vi/TNhnUHrBsJs/maxresdefault.jpg',
    url: 'https://layerre.com/tutorials/make-openai-to-layerre-images/',
    type: 'video.other',
    siteName: 'Layerre',
  },
  '/tutorials/make-json-multilingual-layerre': {
    title:
      'Make Tutorial 3: Use a JSON File to Generate Multilingual Image Variants with Layerre',
    description:
      'Use a JSON file to drive image generation from your Canva design using Layerre and Make. Provide translations or content as JSON, Layerre produces a new image for each entry.',
    image: 'https://img.youtube.com/vi/tmW9Zg5fM34/maxresdefault.jpg',
    url: 'https://layerre.com/tutorials/make-json-multilingual-layerre/',
    type: 'video.other',
    siteName: 'Layerre',
  },
  '/tutorials/zapier-google-sheets-to-layerre-to-drive': {
    title:
      'Zapier Tutorial 1: Create Bulk Image Variants from Google Sheets and Save to Google Drive',
    description:
      'Use Zapier to pull rows from Google Sheets, generate image variants from your Canva design using Layerre, and automatically save results to Google Drive.',
    image: 'https://img.youtube.com/vi/nNOKQNp7JQI/maxresdefault.jpg',
    url: 'https://layerre.com/tutorials/zapier-google-sheets-to-layerre-to-drive/',
    type: 'video.other',
    siteName: 'Layerre',
  },
  '/tutorials/zapier-typeform-to-layerre': {
    title:
      'Zapier Tutorial 2: Turn Typeform Responses into Image Variants Using Layerre',
    description:
      'Connect Typeform with Layerre using Zapier to generate image variants from new form submissions. Create a template from Canva and generate a unique image per response.',
    image: 'https://img.youtube.com/vi/-Hex4JVc3P4/maxresdefault.jpg',
    url: 'https://layerre.com/tutorials/zapier-typeform-to-layerre/',
    type: 'video.other',
    siteName: 'Layerre',
  },
  '/tutorials/zapier-airtable-to-layerre': {
    title:
      'Zapier Tutorial 3: Turn Airtable Records into Image Variants Using Layerre',
    description:
      'Connect Airtable with Layerre using Zapier to generate image variants from your records. Create a template from Canva, list Airtable records, and generate images.',
    image: 'https://img.youtube.com/vi/v8hvIWl7pL8/maxresdefault.jpg',
    url: 'https://layerre.com/tutorials/zapier-airtable-to-layerre/',
    type: 'video.other',
    siteName: 'Layerre',
  },
  '/blog/5-canva-design-automation-tools-to-scale-visual-content-production': {
    title:
      '5 Canva Design Automation Tools to Scale Visual Content Production | Layerre',
    description:
      'Stop manually creating hundreds of design variations. Here are the tools that actually work for automating Canva designs at scale.',
    image:
      'https://layerre.com/blogs/5-canva-design-automation-tools-to-scale-visual-content-production/1.jpeg',
    url: 'https://layerre.com/blog/5-canva-design-automation-tools-to-scale-visual-content-production/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/scale-ad-creative-generation': {
    title: 'The 4-Step Workflow to Scale Ad Creative Generation 10x | Layerre',
    description:
      'Stop wasting time on manual design production. Learn the proven 4-step workflow that lets marketing teams generate hundreds of ad variations instantly.',
    image: 'https://layerre.com/blogs/scale-ad-creative-generation/1.jpeg',
    url: 'https://layerre.com/blog/scale-ad-creative-generation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/using-ai-in-design-automation': {
    title: 'Using AI in Design Automation | Layerre',
    description:
      'Most AI workflows stop at generation. Learn how to complete the journey from prompt engineering and model selection to automated design production with Layerre.',
    image: 'https://layerre.com/blogs/using-ai-in-design-automation/1.png',
    url: 'https://layerre.com/blog/using-ai-in-design-automation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/integrations/make': {
    title: 'Layerre is Now Available on Make | Layerre',
    description:
      'Connect Layerre with thousands of apps using Make. Automate design generation workflows without code.',
    image: 'https://layerre.com/blogs/make-integration/hero.png',
    url: 'https://layerre.com/integrations/make/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/integrations/zapier': {
    title: 'Layerre is Now Available on Zapier (Beta) | Layerre',
    description:
      'Connect Layerre with 8,000+ apps using Zapier. Automate design generation workflows without code. Now in Beta!',
    image: 'https://layerre.com/blogs/zapier-integration/hero.png',
    url: 'https://layerre.com/integrations/zapier/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/integrations/n8n': {
    title: 'Layerre is Now Available on n8n | Layerre',
    description:
      'Create templates and variants from Canva designs with the Layerre node for n8n. Install from npm and automate design workflows.',
    image: 'https://layerre.com/blogs/n8n-integration/hero.png',
    url: 'https://layerre.com/integrations/n8n/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/use-cases/email-certificate-automation': {
    title: 'How to Automate Personalized Certificate Emails at Scale | Layerre',
    description:
      'Learn how to integrate Layerre into your email marketing pipeline to automatically generate and send personalized certificates to course completers, event attendees, and training participants.',
    image: 'https://layerre.com/blogs/email-certificate-automation/1.png',
    url: 'https://layerre.com/use-cases/email-certificate-automation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/use-cases/instagram-automation': {
    title: 'How to Automate Instagram Post Creation at Scale | Layerre',
    description:
      'Learn how to use Layerre to automatically generate personalized Instagram posts from spreadsheets and AI, scaling your social media content production from manual creation to automated workflows.',
    image: 'https://layerre.com/blogs/instagram-automation/1.png',
    url: 'https://layerre.com/use-cases/instagram-automation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/use-cases/ad-creation-funnel-ads': {
    title:
      'Ad Creation Automation: Top, Middle, Bottom of Funnel Ads | Layerre',
    description:
      'Learn how to scale ad creation for all funnel stages using Layerre. Generate TOFU, MOFU, and BOFU ad variations automatically with AI-powered copy and visual generation.',
    image: 'https://layerre.com/blogs/ad-creation-funnel-ads/1.png',
    url: 'https://layerre.com/use-cases/ad-creation-funnel-ads/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare': {
    title: 'Compare Layerre with Design Automation Tools | Layerre',
    description:
      'See how Layerre compares to other Canva design automation and image generation tools. Side-by-side comparisons.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-bannerbear': {
    title: 'Layerre vs BannerBear: Canva Import vs Template Editor',
    description:
      'Side-by-side: Layerre imports Canva designs for API generation; BannerBear uses its own editor. Compare pricing, video support, and which fits your workflow.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-bannerbear/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-orshot': {
    title: 'Layerre vs Orshot | Compare Design Automation APIs | Layerre',
    description:
      'Compare Layerre and Orshot for automated image, PDF, and video generation. Canva import, API, and features side by side.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-orshot/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-templated': {
    title: 'Layerre vs Templated | Compare Image Generation APIs | Layerre',
    description:
      'Compare Layerre and Templated for automated image generation. Canva import vs built-in editor, API, and pricing.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-templated/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-canva-api': {
    title: 'Layerre vs Canva Connect API: No Enterprise Required',
    description:
      'Canva Connect API needs Enterprise and a 3-step async workflow. Layerre renders Canva templates in one API call on any plan. Full feature comparison inside.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-canva-api/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-canva-bulk-create': {
    title: 'Layerre vs Canva Bulk Create: API vs CSV Upload',
    description:
      'Canva Bulk Create caps at ~100 variants with no API. Layerre automates the same Canva templates via REST API with color, font, and Zapier support. Compare features.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-canva-bulk-create/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-storyteq': {
    title: 'Layerre vs Storyteq: Self-Serve API vs Enterprise DAM',
    description:
      'Storyteq targets enterprise content ops with DAM and approvals. Layerre imports Canva designs and generates via API from $15/mo. See which fits your team.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-storyteq/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-shotstack': {
    title: 'Layerre vs Shotstack: Canva Templates vs JSON Video API',
    description:
      'Need image automation from Canva designs? Compare Layerre (Canva import, static images) vs Shotstack (JSON video templates). Pricing, API, and use cases.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-shotstack/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-creatopy': {
    title: 'Layerre vs Creatopy: Canva API vs Ad Editor Platform',
    description:
      'Creatopy is a full ad editor with campaign management. Layerre imports Canva designs and generates via API. Compare pricing, automation depth, and setup time.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-creatopy/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-renderform': {
    title: 'Layerre vs Renderform: Canva Import vs Built-In Editor',
    description:
      'Renderform requires rebuilding templates in its editor. Layerre imports existing Canva designs. Compare API pricing, dynamic URLs, and generation limits.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-renderform/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-placid': {
    title: 'Layerre vs Placid: Canva Designs vs Placid Templates',
    description:
      'Placid uses its own template editor; Layerre imports Canva designs directly. Compare image and video generation, API pricing, and no-code integrations.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-placid/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-abyssale': {
    title: 'Layerre vs Abyssale: Canva Import vs Marketing Studio',
    description:
      'Abyssale is a marketing creative studio with its own editor. Layerre imports Canva designs for API batch generation. Compare features, pricing, and setup.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-abyssale/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-creatomate': {
    title: 'Layerre vs Creatomate: Canva Images vs JSON Video API',
    description:
      'Creatomate renders JSON-defined video and image templates. Layerre imports Canva designs for static image generation. Compare API, pricing, and workflows.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-creatomate/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-thumbapi': {
    title: 'Layerre vs ThumbAPI: Canva Templates vs AI Title-to-Image',
    description:
      'ThumbAPI generates thumbnails from titles with AI. Layerre imports Canva designs for layer-accurate API renders. Compare pricing, formats, and workflow fit.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-thumbapi/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-snapshark': {
    title: 'Layerre vs SnapShark: Native Canva Import vs Editor API',
    description:
      'SnapShark uses its template editor with AI Canva/Figma import from $9/mo. Layerre imports Canva designs natively. Compare features, credits, and pricing.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-snapshark/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-switchboard': {
    title:
      'Layerre vs Switchboard Canvas: Canva Import vs Responsive Templates',
    description:
      'Switchboard Canvas renders multi-size responsive templates from $19/mo. Layerre imports Canva designs for API image generation. Compare plans and features.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-switchboard/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-apitemplate': {
    title: 'Layerre vs APITemplate.io: Canva Import vs Built-In Editors',
    description:
      'APITemplate.io generates images and PDFs from its editors. Layerre imports Canva designs for image and PDF automation. Compare free tiers, pricing, and integrations.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-apitemplate/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-duply': {
    title: 'Layerre vs Duply: Canva Image API vs Image+Video Automation',
    description:
      'Duply generates images and videos from its editor from $26/mo. Layerre imports Canva designs for image API automation. Compare pricing, Canva support, and fit.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-duply/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-dynapictures': {
    title: 'Layerre vs DynaPictures: Canva Import vs Editor-First API',
    description:
      'DynaPictures generates images via API, URL params, and spreadsheets from its editor. Layerre imports Canva designs. Compare pricing, integrations, and fit.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-dynapictures/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/compare/layerre-vs-pixelixe': {
    title: 'Layerre vs Pixelixe: Canva Automation vs Studio + White-Label',
    description:
      'Pixelixe offers Studio, APIs, and white-label editing from ~$59 Automate. Layerre imports Canva for image/PDF APIs. Compare workflows and pricing.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/compare/layerre-vs-pixelixe/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives': {
    title: 'Design Automation Tool Alternatives | Layerre',
    description:
      'Looking for an alternative to your current design automation tool? Browse comparisons of the top options for automating Canva design generation.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/alternatives/bannerbear-alternatives': {
    title: 'Best Bannerbear Alternative That Keeps Your Canva Templates',
    description:
      'Bannerbear alternative with Canva import: Layerre from $15/mo plus a free 50-credit tier. Keep Canva templates, add API bulk generation. No editor rebuild.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/bannerbear-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/orshot-alternatives': {
    title: 'Orshot Alternative That Imports a Canva Link',
    description:
      'Orshot alternative for Canva-first teams: import a Canva share link, generate images and PDFs via API. Free 50 credits, then $15/mo. Stay with Orshot if you need Figma and video in one product.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/orshot-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/templated-alternatives': {
    title: 'Best Templated Alternative for Canva Bulk Image Generation',
    description:
      'Templated alternative from $15/mo: import Canva share links, generate bulk images via API, and start free with 50 credits. No second template editor.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/templated-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/canva-api-alternatives': {
    title: 'Canva API Pricing vs Cheaper Alternatives | Layerre',
    description:
      'Canva API pricing: Pro/Business seats plus Enterprise contact sales. Cheaper bulk alternative: Layerre free (50 credits) or $15/mo for 500 credits.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/canva-api-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/canva-bulk-create-alternatives': {
    title: 'Canva Bulk Create Alternatives with API and No CSV Row Cap',
    description:
      'Canva Bulk Create alternative with an API: import the same Canva template, map a Google Sheet, generate bulk images. No CSV row cap. Free 50 credits, then $15/mo.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/canva-bulk-create-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/storyteq-alternatives': {
    title: 'Storyteq Alternative with Self-Serve Canva Import',
    description:
      'Storyteq alternative for teams that design in Canva: import a share link and generate via API without an enterprise creative ops contract. Free 50 credits, then $15/mo.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/storyteq-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/shotstack-alternatives': {
    title: 'Shotstack Alternative for Image Output from Canva',
    description:
      'Shotstack alternative when you need still images and PDFs from Canva, not video timelines. Import a Canva link, generate via API. Free 50 credits, then $15/mo.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/shotstack-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/creatopy-alternatives': {
    title: 'Creatopy Alternative for Canva Ad Creatives',
    description:
      'Creatopy alternative if your ads are designed in Canva: import the share link and batch-generate variants via API. Free 50 credits, then $15/mo.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/creatopy-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/renderform-alternatives': {
    title: 'Renderform Alternative That Keeps Your Canva File',
    description:
      'Renderform alternative for Canva templates: paste a share link, map layers, generate via API or Zapier. Free 50 credits, then $15/mo. No rebuild in another editor.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/renderform-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/placid-alternatives': {
    title: 'Best Placid Alternative If You Design in Canva? | Layerre',
    description:
      'Placid alternative for Canva teams: keep your Canva templates, generate via API from $15/mo, 50 free credits. No rebuild in the Placid editor.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/placid-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/abyssale-alternatives': {
    title: 'Abyssale Alternative for Canva Templates',
    description:
      'Abyssale alternative for batch ads from Canva: import designs, generate variants via API, connect Zapier or Make. Free 50 credits, then $15/mo. No enterprise contract required.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/abyssale-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/creatomate-alternatives': {
    title: 'Creatomate Alternative for Images from Canva',
    description:
      'Creatomate alternative when you need still images and PDFs from Canva, not template video. Free 50 credits, then $15/mo. Stay with Creatomate for video timelines.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/creatomate-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/thumbapi-alternatives': {
    title: 'Best ThumbAPI Alternatives for Template-Based Image APIs',
    description:
      'Compare ThumbAPI alternatives that use Canva templates instead of AI title-to-image. Layerre, SnapShark, BannerBear, and more with pricing.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/thumbapi-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/snapshark-alternatives': {
    title: 'Best SnapShark Alternatives with Canva Design Automation',
    description:
      'Looking past SnapShark? Compare alternatives for template image APIs, including native Canva import, pricing, and no-code integrations.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/snapshark-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/switchboard-alternatives': {
    title: 'Switchboard Canvas Alternative with Canva Import',
    description:
      'Switchboard Canvas alternative for automated images from Canva share links and self-serve pricing. Free 50 credits, then $15/mo.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/switchboard-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/apitemplate-alternatives': {
    title: 'APITemplate.io Alternative That Imports Canva',
    description:
      'APITemplate.io alternative for teams that design in Canva: import a share link instead of rebuilding templates. Free 50 credits, then $15/mo.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/apitemplate-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/duply-alternatives': {
    title: 'Best Duply Alternatives for Canva Image Automation | Layerre',
    description:
      'Compare Duply alternatives for automated images from templates. See Canva-first options, free tiers, and API pricing side by side.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/duply-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/dynapictures-alternatives': {
    title: 'Best DynaPictures Alternatives with Canva Import | Layerre',
    description:
      'Compare DynaPictures alternatives for template image APIs. See Canva-first options, n8n/Make/Zapier support, and self-serve pricing.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/dynapictures-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/pixelixe-alternatives': {
    title: 'Best Pixelixe Alternatives for Design Automation | Layerre',
    description:
      'Looking past Pixelixe Studio? Compare alternatives for Canva-based image APIs, spreadsheet automation, and simpler self-serve pricing.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/pixelixe-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/canva-bulk-create': {
    title: 'Canva Bulk Create: Row Limit, Images, and When to Use an API',
    description:
      'Canva Bulk Create fills designs from a CSV in the editor. ~100 row practical cap, public image URLs required, no API, included with Pro/Business. When to switch to an API.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/canva-bulk-create/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/canva-connect-api': {
    title: 'What Is the Canva Connect API? Limits, Pricing & Alternatives',
    description:
      'Canva Connect Autofill is on Pro, Business, and Enterprise (not Free). Async text/image jobs; Enterprise still for SSO. Compare with self-serve render APIs.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/canva-connect-api/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/open-graph-image': {
    title: 'What Is an Open Graph Image? Size, Examples & Dynamic OG Guide',
    description:
      'OG images control how links look on social and messaging apps. Learn the 1200×630 standard, static vs dynamic generation, and how to automate unique OG images per page.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/open-graph-image/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/dynamic-template': {
    title: 'What Is a Dynamic Template? Design Automation Explained',
    description:
      'Dynamic templates have swappable text, image, and color fields for programmatic generation. Learn how to build them in Canva and generate unlimited variants via API.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/dynamic-template/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/image-generation-api': {
    title: 'What Is an Image Generation API? Template-Based vs AI Explained',
    description:
      'Image generation APIs render designs from templates and data via HTTP. Learn how they differ from AI image tools, typical API contracts, and who uses them.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/image-generation-api/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/creative-automation': {
    title: 'What Is Creative Automation? Platforms, Benefits & When to Adopt',
    description:
      'Creative automation scales image, video, and document production from templates and data. Learn the automation spectrum, top platforms, and when your team should adopt it.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/creative-automation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/ad-creative-automation': {
    title: 'What Is Ad Creative Automation? Scale Ads Without Manual Design',
    description:
      'Generate hundreds of ad variants from templates and data for A/B testing and multi-market campaigns. Learn the workflow, scale math, and top tools.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/ad-creative-automation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/design-automation': {
    title: 'What Is Design Automation? How Template-Based Generation Works',
    description:
      'Design automation generates images, PDFs, and banners from templates and data, no manual editing per variant. Learn components, use cases, and how it differs from AI.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/design-automation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/bulk-image-generation': {
    title: 'What Is Bulk Image Generation? Scale Visuals from Templates',
    description:
      'Bulk image generation produces hundreds or thousands of design variants from one template and a data source. Learn how it works, typical batch sizes, and tools.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/bulk-image-generation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/render-credits': {
    title: 'What Are Render Credits? How Design Automation Billing Works',
    description:
      'Render credits meter image and PDF generation on design automation platforms. Learn how credits work, cost per render, and how Layerre plans compare.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/render-credits/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/can-you-use-canva-api-without-enterprise': {
    title: "Can You Use the Canva API Without Enterprise? Yes, Here's How",
    description:
      'Canva Connect API requires Enterprise. Learn how to automate Canva designs via API on any Canva plan using Layerre, no sales call, 50 free credits/mo.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/can-you-use-canva-api-without-enterprise/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-use-zapier-with-canva-design-automation': {
    title: 'How to Use Zapier with Canva Design Automation (Step-by-Step)',
    description:
      'Connect Zapier to Layerre to auto-generate Canva design variants from Google Sheets, Shopify, or forms. Setup guide with example Zaps for certificates and OG images.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-use-zapier-with-canva-design-automation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-migrate-from-canva-bulk-create-to-layerre': {
    title: 'How to Migrate from Canva Bulk Create to Layerre (Step-by-Step)',
    description:
      'Move from manual CSV uploads to API automation. Keep your Canva templates, import into Layerre, connect Zapier or Make, and generate unlimited variants programmatically.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-migrate-from-canva-bulk-create-to-layerre/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-generate-pdfs-from-canva-templates': {
    title: 'How to Generate PDFs from Canva Templates (API & No-Code)',
    description:
      'Import a Canva design into Layerre and export certificates, invoices, and documents as PDF via API, Make, Zapier, or n8n. Step-by-step guide.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-generate-pdfs-from-canva-templates/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-generate-images-from-csv': {
    title: 'How to Generate Images from a CSV File (Bulk Template Guide)',
    description:
      'Map CSV columns to Canva template layers and bulk-generate images via Google Sheets, API loops, or automation tools. Includes Bulk Create vs API.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-generate-images-from-csv/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/template-based-vs-ai-image-generation': {
    title: 'Template-Based vs AI Image Generation: Which API Do You Need?',
    description:
      'Template APIs deliver on-brand, deterministic designs. AI APIs invent pixels from prompts. Learn the difference and when to use Layerre vs generative models.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/template-based-vs-ai-image-generation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-generate-multiple-image-sizes-at-once': {
    title: 'How to Generate Multiple Image Sizes at Once from One Template',
    description:
      'Produce 1:1, 4:5, 9:16, and landscape variants from the same campaign data. Canva size templates, multi-page imports, and automation tips.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-generate-multiple-image-sizes-at-once/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-automate-multi-page-canva-designs': {
    title: 'How to Automate Multi-Page Canva Designs (Carousels & PDFs)',
    description:
      'Import multi-page Canva files into Layerre to automate Instagram carousels, documents, and slide packs without rebuilding each page elsewhere.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-automate-multi-page-canva-designs/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/build-vs-buy-image-generation-api': {
    title: 'Build vs Buy an Image Generation API: Decision Guide',
    description:
      'Compare building an in-house renderer vs buying a template API. Cost, maintenance, Canva workflows, and when Layerre is the faster path.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/build-vs-buy-image-generation-api/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-migrate-from-bannerbear': {
    title: 'How to Migrate from BannerBear to Layerre (Step-by-Step)',
    description:
      'Move BannerBear templates to Canva, import into Layerre, remap modifications to layer overrides, and cut over Zapier/Make or API calls.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-migrate-from-bannerbear/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/is-canva-api-free': {
    title: 'Is the Canva API Free? Enterprise vs Alternatives',
    description:
      'Canva Connect API requires Enterprise, not a free developer API. Learn free/Pro options for automating Canva designs with Layerre.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/is-canva-api-free/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-use-canva-bulk-create': {
    title: 'How to Use Canva Bulk Create (Step by Step)',
    description:
      'How to use Canva Bulk Create with CSV data, what it is good for, and when to switch to API automation with Layerre.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-use-canva-bulk-create/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/canva-bulk-create-limits': {
    title: 'Canva Bulk Create Limits: Batch Size, API & Workarounds',
    description:
      'Practical limits of Canva Bulk Create, no API, batch size, field types, and how Layerre removes the manual CSV loop.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/canva-bulk-create-limits/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/what-is-canva-connect-api-autofill': {
    title: 'What Is Canva Connect API Autofill?',
    description:
      'Canva Connect API autofill fills brand templates via API but requires Enterprise. See how it works and non-Enterprise alternatives.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/what-is-canva-connect-api-autofill/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-migrate-from-placid': {
    title: 'How to Migrate from Placid to Layerre',
    description:
      'Migrate from Placid to Layerre: rebuild in Canva, remap fields, swap API or Zapier modules, and parallel-run fixtures.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-migrate-from-placid/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-migrate-from-orshot': {
    title: 'How to Migrate from Orshot to Layerre',
    description:
      'Step-by-step Orshot to Layerre migration: Canva rebuild, layer mapping, and automation cutover.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-migrate-from-orshot/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-migrate-from-creatomate': {
    title: 'How to Migrate from Creatomate to Layerre',
    description:
      'Move image/PDF workloads from Creatomate to Layerre with Canva templates while keeping video on a dedicated tool.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-migrate-from-creatomate/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-migrate-from-renderform': {
    title: 'How to Migrate from RenderForm to Layerre',
    description:
      'Migrate RenderForm templates to Layerre via Canva import, field mapping, and API/no-code cutover.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-migrate-from-renderform/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-migrate-from-templated': {
    title: 'How to Migrate from Templated.io to Layerre',
    description:
      'Switch from Templated.io to Layerre: recreate designs in Canva, remap layers, and update API integrations.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-migrate-from-templated/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-automate-canva-with-google-sheets': {
    title: 'How to Automate Canva Designs with Google Sheets',
    description:
      'Connect Google Sheets to Canva design automation via Layerre, Zapier, Make, or n8n, one row per image variant.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-automate-canva-with-google-sheets/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/is-canva-bulk-create-free': {
    title: 'Is Canva Bulk Create Free? Pro vs Free Plan',
    description:
      'Canva Bulk Create is a Pro/Teams feature, not on Canva Free. See what “bulk create free” means and how to generate from Canva templates without Enterprise.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/is-canva-bulk-create-free/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-add-images-to-canva-bulk-create': {
    title: 'How to Add Images to Canva Bulk Create (Image URLs)',
    description:
      'Map a public image URL column to a Canva frame in Bulk Create. Fix Drive links, CSV mistakes, and when to automate photos with Layerre.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-add-images-to-canva-bulk-create/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-use-canva-bulk-create-csv': {
    title: 'How to Use a CSV with Canva Bulk Create',
    description:
      'CSV setup for Canva Bulk Create: header row, one variant per row, UTF-8, image URL columns, and when to switch to a live Google Sheet plus API.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-use-canva-bulk-create-csv/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-much-does-canva-api-cost': {
    title: 'How Much Does the Canva API Cost? Pro, Enterprise & Alternatives',
    description:
      'Canva Autofill sits on Pro, Business, or Enterprise seats (Enterprise is contact sales). Compare with Layerre: 50 free credits or $15/mo.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-much-does-canva-api-cost/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/canva-autofill': {
    title: 'What Is Canva Autofill? How to Bulk Fill Canva Designs',
    description:
      'Canva Autofill fills Brand Templates via the Connect API (Pro+). How to bulk fill Canva designs with a share link, sheet columns, and an API.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/canva-autofill/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/template-based-rendering': {
    title: 'What Is Template-Based Rendering? Design Automation Explained',
    description:
      'Template-based rendering combines a fixed design with variable data for deterministic images and PDFs. How it differs from AI image generation.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/template-based-rendering/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/brand-template': {
    title: 'What Is a Brand Template? Governance for Design Automation',
    description:
      'Brand templates lock logos, fonts, and layouts while allowing approved fields to change. Learn Canva Brand Templates and automation best practices.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/brand-template/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/multipage-template': {
    title: 'What Is a Multipage Template? Carousels, PDFs & Automation',
    description:
      'Multipage templates power social carousels and multi-page PDFs from one design file. Learn automation patterns and Canva multi-page imports.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/multipage-template/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/creative-operations': {
    title: 'What Is Creative Operations? Scaling Design Production',
    description:
      'Creative ops runs briefs, templates, approvals, localization, and delivery as a system. See where design automation APIs fit the workflow.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/creative-operations/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/layer-override': {
    title: 'What Is a Layer Override? Template API Personalization',
    description:
      'Layer overrides replace text, images, and other template properties at render time. Learn payloads, naming tips, and debugging practices.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/layer-override/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/variable-data-printing': {
    title: 'What Is Variable Data Printing (VDP)? From Print to APIs',
    description:
      'Variable data printing personalizes each print piece from a template and database. See how modern image/PDF APIs extend VDP to digital channels.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/variable-data-printing/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/og-image-size': {
    title: 'OG Image Size: Recommended Dimensions (1200×630)',
    description:
      'Recommended Open Graph image sizes by platform (1200×630 default) and how to generate correctly sized OG images at scale.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/og-image-size/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/batch-generation': {
    title: 'What Is Batch Generation? Bulk Design Rendering Explained',
    description:
      'Batch generation renders many design variants from a spreadsheet or payload list. Compare Bulk Create vs API batch workflows.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/batch-generation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/image-personalization': {
    title: 'What Is Image Personalization? Templates + Data',
    description:
      'Image personalization creates unique visuals per recipient or product from one template. See channels, fields, and API workflows.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/image-personalization/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/image-template-api': {
    title: 'What Is an Image Template API?',
    description:
      'An image template API turns a design template plus data into rendered images or PDFs. Compare Canva Autofill and Layerre.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/image-template-api/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/creative-ops-tools': {
    title: 'Creative Ops Tools: Stack for Scalable Visual Production',
    description:
      'Creative ops tools cover briefing, templates, generation, and delivery. See where design automation APIs fit the stack.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/creative-ops-tools/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/canva-automation': {
    title: 'What Is Canva Automation? Bulk Create, API & Zapier',
    description:
      'Canva automation means generating variants from a Canva template via Bulk Create, Connect Autofill, or a third-party API. Compare options without Enterprise.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/canva-automation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/glossary/automated-certificate-generator': {
    title: 'What Is an Automated Certificate Generator?',
    description:
      'An automated certificate generator fills a Canva template from a spreadsheet or LMS and exports named PDFs. See Bulk Create vs API workflows.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/glossary/automated-certificate-generator/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/canva-automation-api-guide': {
    title: 'Canva Automation & API Guide: Bulk Create, Connect API & Layerre',
    description:
      'Complete guide to automating Canva designs: Bulk Create limits, Connect API requirements, and how to generate variants via API without Enterprise. All options compared.',
    image: 'https://layerre.com/blogs/canva-automation-api-guide/1.png',
    url: 'https://layerre.com/blog/canva-automation-api-guide/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/open-graph-image-guide': {
    title: 'Open Graph Image Size: 1200×630',
    description:
      'The Open Graph image size to use is 1200×630. Platform table, how to check yours, and how to generate a unique card per page from Canva.',
    image: 'https://layerre.com/blogs/open-graph-image-guide/1.jpeg',
    url: 'https://layerre.com/blog/open-graph-image-guide/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/creative-automation-guide': {
    title: 'Creative Automation Guide: Scale Visual Production',
    description:
      'What creative automation means for marketing teams, how it differs from design automation, and how to scale ad and social creatives with Canva + Layerre.',
    image: 'https://layerre.com/blogs/creative-automation-guide/1.jpeg',
    url: 'https://layerre.com/blog/creative-automation-guide/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/n8n-canva-automation': {
    title: 'Automate Canva Designs with n8n and Layerre',
    description:
      'Step-by-step guide to automating Canva design generation in n8n without Canva Enterprise. Import templates into Layerre and map spreadsheet data.',
    image: 'https://layerre.com/blogs/n8n-canva-automation/1.jpeg',
    url: 'https://layerre.com/blog/n8n-canva-automation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/canva-connect-api-autofill-guide': {
    title: 'Canva Connect API Autofill Explained (+ Non-Enterprise Options)',
    description:
      'How Canva Connect API autofill and brand templates work, Enterprise requirements, and how to automate Canva designs without Enterprise using Layerre.',
    image: 'https://layerre.com/blogs/canva-connect-api-autofill-guide/1.jpeg',
    url: 'https://layerre.com/blog/canva-connect-api-autofill-guide/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/use-cases/bulk-image-generation-from-spreadsheet': {
    title: 'Bulk Image Generation from Spreadsheets & Canva Templates',
    description:
      'Generate hundreds of images from Google Sheets or CSV using Canva templates. Bulk Create limits, batch workflows, and API automation with Layerre.',
    image:
      'https://layerre.com/blogs/bulk-image-generation-from-spreadsheet/1.jpeg',
    url: 'https://layerre.com/use-cases/bulk-image-generation-from-spreadsheet/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/placid-pricing-alternatives': {
    title: 'Placid Pricing Explained (2026) & Best Placid Alternatives',
    description:
      'How Placid pricing works for template image APIs and the best Placid alternatives, including Layerre for Canva-native design automation.',
    image: 'https://layerre.com/blogs/placid-pricing-alternatives/1.jpeg',
    url: 'https://layerre.com/blog/placid-pricing-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/switch-from-bannerbear': {
    title: 'Switching from Bannerbear to Canva-Native Automation',
    description:
      'Why teams leave Bannerbear for deeper automation or Canva templates, migration checklist, and how Layerre compares as a Bannerbear alternative.',
    image: 'https://layerre.com/blogs/switch-from-bannerbear/1.jpeg',
    url: 'https://layerre.com/blog/switch-from-bannerbear/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/from-canva-template-to-customers': {
    title: 'From a Canva Template to Customers | Layerre',
    description:
      'You have a Canva design. Here is a simple path: spreadsheet of copy, languages, and photos, generate each version in Layerre, schedule posts, then LinkedIn and email that match the graphic.',
    image: 'https://layerre.com/blogs/from-canva-template-to-customers/1.png',
    url: 'https://layerre.com/blog/from-canva-template-to-customers/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/best-bulk-image-generators': {
    title: 'Best Bulk Image Generators in 2026 (Template vs AI)',
    description:
      'Compare bulk image generators: template-based tools vs AI batch generators, Canva Bulk Create limits, and when a Canva-native API like Layerre is the better fit.',
    image: 'https://layerre.com/blogs/best-bulk-image-generators/1.png',
    url: 'https://layerre.com/blog/best-bulk-image-generators/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/zapier-canva-automation': {
    title: 'Automate Canva Designs with Zapier (No Enterprise)',
    description:
      'Zapier Canva automation without Canva Enterprise: import a Canva template into Layerre, map Google Sheets or form data, and generate images in a Zap.',
    image: 'https://layerre.com/blogs/zapier-canva-automation/1.png',
    url: 'https://layerre.com/blog/zapier-canva-automation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/canva-bulk-create-with-images': {
    title: 'Canva Bulk Create with Images & Image URLs',
    description:
      'Add photos to Canva Bulk Create using image URLs or files, fix common CSV mistakes, and automate the same Canva template in Layerre when the batch is recurring.',
    image: 'https://layerre.com/blogs/canva-bulk-create-with-images/1.png',
    url: 'https://layerre.com/blog/canva-bulk-create-with-images/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/custom-og-image-template': {
    title: 'How to Create a Custom OG Image Template | Layerre',
    description:
      'Build a 1200×630 Open Graph template in Canva and generate a unique og:image per page. Compare static files, Vercel OG, Bannerbear, Placid, and Layerre.',
    image: 'https://layerre.com/blogs/custom-og-image-template/1.png',
    url: 'https://layerre.com/blog/custom-og-image-template/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/link-preview-og-twitter-card-generators-compared': {
    title: 'Link Preview and Twitter Card Generators Compared | Layerre',
    description:
      'Metadata scrapers, card designers, and image APIs do different jobs. Compare OpenGraph.xyz, Vercel OG, Bannerbear, Placid, and Layerre.',
    image:
      'https://layerre.com/blogs/link-preview-og-twitter-card-generators-compared/1.jpg',
    url: 'https://layerre.com/blog/link-preview-og-twitter-card-generators-compared/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/og-template-sizes': {
    title: 'OG Template Sizes and Starting Points | Layerre',
    description:
      'Open Graph template sizes that actually get used: 1200×630 first. Design the card in Canva and generate one image per URL.',
    image: 'https://layerre.com/blogs/og-template-sizes/1.png',
    url: 'https://layerre.com/blog/og-template-sizes/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/og-card-preview': {
    title: 'OG Image Size Checker',
    description:
      'Paste a page URL to check the live og:image against 1200×630, see the card crop, and generate one replacement from a Canva template.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/og-card-preview/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/og-image-sizes': {
    title: 'OG Image Sizes by Platform (1200×630)',
    description:
      'Recommended Open Graph and social preview image sizes for Facebook, LinkedIn, X, Slack, and Discord. Default to 1200×630.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/og-image-sizes/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/bulk-create-csv-checker': {
    title: 'Canva Bulk Create CSV Checker',
    description:
      'Paste or upload your Canva Bulk Create CSV. Check row count vs the ~100-row cap, empty cells, and image URL columns.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/bulk-create-csv-checker/',
    type: 'website',
    siteName: 'Layerre',
  },
  '/blog/bulk-certificate-generator-canva': {
    title: 'Bulk Certificate Generator from a Canva Template | Layerre',
    description:
      'Automatic certificate generator for courses, events, and awards. One Canva template, a spreadsheet of names, PDF or image per row. Free 50 credits or $15/mo.',
    image: 'https://layerre.com/blogs/bulk-certificate-generator-canva/1.png',
    url: 'https://layerre.com/blog/bulk-certificate-generator-canva/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/certificate-templates-canva-bulk': {
    title: 'Certificate Templates You Can Fill in Bulk | Layerre',
    description:
      'Course, attendance, and award certificate templates in Canva, filled from a spreadsheet instead of editing each name by hand.',
    image: 'https://layerre.com/blogs/certificate-templates-canva-bulk/1.png',
    url: 'https://layerre.com/blog/certificate-templates-canva-bulk/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-generate-certificates-in-bulk': {
    title: 'How to Generate Certificates in Bulk | Layerre',
    description:
      'Generate certificates in bulk from a Canva template and a spreadsheet. Compare Canva Bulk Create, Certifier, and Layerre for classes and events.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-generate-certificates-in-bulk/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-mail-merge-in-canva': {
    title: 'Can You Mail Merge in Canva? Bulk Create Steps | Layerre',
    description:
      'Yes. Canva mail merge is the Bulk Create app on Pro, Teams, or Enterprise. Steps, limits, and the API alternative when you outgrow the editor.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-mail-merge-in-canva/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/blog/branded-pitch-decks-at-scale': {
    title: 'Branded Pitch Decks & Proposals at Scale | Layerre',
    description:
      'Design pitch decks, one-pagers, and proposals once in Canva, generate branded variants at scale with Layerre, then share and track them with SendNow.',
    image: 'https://layerre.com/blogs/branded-pitch-decks-at-scale/1.png',
    url: 'https://layerre.com/blog/branded-pitch-decks-at-scale/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/use-cases/auto-generate-certificates-from-spreadsheet': {
    title: 'Auto Generate Certificates from a Spreadsheet',
    description:
      'Automated certificate generator from Google Sheets or CSV: Canva template, name and date fields, bulk PDFs, and email without designing each certificate.',
    image:
      'https://layerre.com/blogs/auto-generate-certificates-from-spreadsheet/1.png',
    url: 'https://layerre.com/use-cases/auto-generate-certificates-from-spreadsheet/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/alternatives/polotno-alternatives': {
    title: 'Polotno Studio Alternatives if You Design in Canva | Layerre',
    description:
      'Polotno Studio is an editor and SDK. Layerre imports Canva designs and generates images via API. Free 50 credits, then $15/mo.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/alternatives/polotno-alternatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/integrations/mcp': {
    title: 'MCP + Layerre | Generate Images from Claude, Cursor & VS Code',
    description:
      'Connect Claude, Cursor, VS Code, and other MCP clients to Layerre. Import a Canva design from chat and render images or PDFs with the hosted MCP server.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/integrations/mcp/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/integrations/airtable': {
    title: 'Airtable + Layerre | Generate Images from Records',
    description:
      'Generate images from Airtable records with Layerre. Automate designs when a record is created or updated, via Make, Zapier, or n8n.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/integrations/airtable/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/integrations/google-sheets': {
    title: 'Google Sheets + Layerre | Import in app or automate',
    description:
      'Import Google Sheets in Generate variations, or connect Sheets through Make, Zapier, or n8n so new rows create Canva template variations automatically.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/integrations/google-sheets/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/integrations/typeform': {
    title: 'Typeform + Layerre | Images from form submissions',
    description:
      'Connect Typeform to Layerre with Zapier, Make, or n8n. Map form answers to template layers and generate a personalized image for each submission.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/integrations/typeform/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/integrations/hubspot': {
    title: 'HubSpot + Layerre | Personalized Images from CRM Data',
    description:
      'Generate personalized marketing images from HubSpot contacts, deals, and campaigns with Layerre and a Canva template.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/integrations/hubspot/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/integrations/notion': {
    title: 'Notion + Layerre | Images from a Notion Database',
    description:
      'Generate design variants from Notion database entries with Layerre. Automate visual content from the pages you already keep in Notion.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/integrations/notion/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/integrations/shopify': {
    title: 'Shopify + Layerre | Product Images and Sale Banners',
    description:
      'Generate product images, sale banners, and marketing visuals from Shopify data with Layerre. Map title, price, and image URL to a Canva template.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/integrations/shopify/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/integrations/webflow': {
    title: 'Webflow + Layerre | CMS and OG Images',
    description:
      'Generate OG images and CMS graphics for Webflow from a Canva template with Layerre. One image per CMS item, not a shared meta description.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/integrations/webflow/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-make-employee-welcome-images': {
    title: 'Bulk Welcome Images for New Employees | Layerre',
    description:
      'Customize a welcome image per new hire from one Canva template and a spreadsheet of names, roles, and photos.',
    image: 'https://layerre.com/blogs/from-canva-template-to-customers/1.png',
    url: 'https://layerre.com/faq/how-to-make-employee-welcome-images/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-automate-facebook-ad-creatives': {
    title: 'Facebook Ad Creative from One Canva Template | Layerre',
    description:
      'Generate Facebook ad image variants from one Canva template. Creative production, not ad buying.',
    image: 'https://layerre.com/blogs/scale-ad-creative-generation/1.jpeg',
    url: 'https://layerre.com/faq/how-to-automate-facebook-ad-creatives/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-automate-social-media-carousels': {
    title: 'Automate Social Carousels from a Canva Template | Layerre',
    description:
      'Turn one Canva slide into a social carousel. A sheet of slide copy becomes one image per slide.',
    image: 'https://layerre.com/blogs/instagram-automation/1.png',
    url: 'https://layerre.com/faq/how-to-automate-social-media-carousels/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-make-marketing-banners-in-bulk': {
    title: 'Bulk Marketing Banners from One Canva Design | Layerre',
    description:
      'Design the banner in Canva. Generate sizes, offers, locales, and mockups from a spreadsheet.',
    image: 'https://layerre.com/blogs/ad-creation-funnel-ads/1.png',
    url: 'https://layerre.com/faq/how-to-make-marketing-banners-in-bulk/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-make-id-badges-in-bulk': {
    title: 'Bulk ID Badges and Event Name Tags | Layerre',
    description:
      'Employee, student, and event badges from a Canva template and a name list. Not for fake IDs.',
    image: 'https://layerre.com/blogs/email-certificate-automation/1.png',
    url: 'https://layerre.com/faq/how-to-make-id-badges-in-bulk/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-make-event-tickets-in-bulk': {
    title: 'Bulk Event Tickets and Passes from Canva | Layerre',
    description:
      'One Canva ticket template and a guest list. A PDF or image per attendee for a real event.',
    image: 'https://layerre.com/blogs/branded-pitch-decks-at-scale/1.png',
    url: 'https://layerre.com/faq/how-to-make-event-tickets-in-bulk/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-use-an-image-api-for-lead-generation': {
    title: 'Image APIs for Lead Generation | Layerre',
    description:
      'Render a personalized image per lead from a Canva template and store the image URL on the CRM record.',
    image: 'https://layerre.com/blogs/from-canva-template-to-customers/1.png',
    url: 'https://layerre.com/faq/how-to-use-an-image-api-for-lead-generation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/what-are-the-best-automated-graphics-tools': {
    title: 'Best Tools for Automated Graphics | Layerre',
    description:
      'Compare Layerre, Bannerbear, Placid, Orshot, Creatomate, Templated, and Polotno for automated graphics.',
    image: 'https://layerre.com/blogs/creative-automation-guide/1.jpeg',
    url: 'https://layerre.com/faq/what-are-the-best-automated-graphics-tools/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/what-are-the-best-tools-for-templated-content': {
    title: 'Best Tools for Templated Content | Layerre',
    description:
      'Choose a templated-content tool by output: documents, Canva images, video, or an embedded editor.',
    image: 'https://layerre.com/blogs/best-bulk-image-generators/1.png',
    url: 'https://layerre.com/faq/what-are-the-best-tools-for-templated-content/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-generate-images-from-a-script': {
    title: 'Script Image Generation from a Canva Template | Layerre',
    description:
      'Script images with Python, Node, or curl against a Canva template API instead of drawing pixels yourself.',
    image: 'https://layerre.com/blogs/canva-automation-api-guide/1.png',
    url: 'https://layerre.com/faq/how-to-generate-images-from-a-script/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-automate-picture-creation': {
    title: 'How to Automate Picture Creation | Layerre',
    description:
      'Picture automation means filling a template from data. Design in Canva, map a sheet or API, and generate each image.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-automate-picture-creation/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/faq/how-to-generate-an-image-url': {
    title: 'How to Generate an Image URL | Layerre',
    description:
      'An image URL is a public HTTPS address for a file. Render a Canva template with Layerre and use the URL the API returns.',
    image: 'https://layerre.com/link-card.png',
    url: 'https://layerre.com/faq/how-to-generate-an-image-url/',
    type: 'article',
    siteName: 'Layerre',
  },
  '/use-cases/wordpress-featured-images-from-layerre': {
    title: 'What WordPress Does When You Upload a Layerre Image | Layerre',
    description:
      'Generate a featured image in Layerre from a Canva template, upload it to WordPress, and set it on the post. Here is what WordPress does with that file and a few tips for a smooth upload.',
    image:
      'https://layerre.com/blogs/wordpress-featured-images-from-layerre/1.png',
    url: 'https://layerre.com/use-cases/wordpress-featured-images-from-layerre/',
    type: 'article',
    siteName: 'Layerre',
  },
};

/**
 * Get SEO metadata for a given pathname
 *
 * Supports exact matches and pattern-based fallbacks for dynamic routes
 */
export function getSeoForPath(pathname: string): SeoMetadata {
  // Normalize pathname (remove trailing slash except for root)
  const normalizedPath =
    pathname === '/' ? pathname : pathname.replace(/\/$/, '');

  // Try exact match first
  if (ROUTE_SEO_MAP[normalizedPath]) {
    return ROUTE_SEO_MAP[normalizedPath];
  }

  // Pattern-based fallbacks for dynamic routes

  // Blog posts
  if (normalizedPath.startsWith('/blog/')) {
    const slug = normalizedPath.replace('/blog/', '');
    if (slug) {
      return {
        title: `${slug.replace(/-/g, ' ')} | Layerre Blog`,
        description:
          'Read more about design automation and Canva workflows on Layerre.',
        image: 'https://layerre.com/link-card.png',
        url: `https://layerre.com${normalizedPath}/`,
        type: 'article',
        siteName: 'Layerre',
      };
    }
  }

  // Integration posts
  if (normalizedPath.startsWith('/integrations/')) {
    const slug = normalizedPath.replace('/integrations/', '');
    if (slug) {
      return {
        title: `${slug.replace(/-/g, ' ')} | Layerre Integrations`,
        description:
          'Connect Layerre with your favorite tools and automate design workflows.',
        image: 'https://layerre.com/link-card.png',
        url: `https://layerre.com${normalizedPath}/`,
        type: 'article',
        siteName: 'Layerre',
      };
    }
  }

  // Use case posts
  if (normalizedPath.startsWith('/use-cases/')) {
    const slug = normalizedPath.replace('/use-cases/', '');
    if (slug) {
      return {
        title: `${slug.replace(/-/g, ' ')} | Layerre Use Cases`,
        description:
          'See how Layerre can help you automate your design workflows.',
        image: 'https://layerre.com/link-card.png',
        url: `https://layerre.com${normalizedPath}/`,
        type: 'article',
        siteName: 'Layerre',
      };
    }
  }

  // What's New posts
  if (normalizedPath.startsWith('/whats-new/')) {
    const slug = normalizedPath.replace('/whats-new/', '');
    if (slug) {
      return {
        title: `${slug.replace(/-/g, ' ')} | Layerre What's New`,
        description: 'Product updates and new features from Layerre.',
        image: 'https://layerre.com/link-card.png',
        url: `https://layerre.com${normalizedPath}/`,
        type: 'article',
        siteName: 'Layerre',
      };
    }
  }

  // Tutorial posts
  if (normalizedPath.startsWith('/tutorials/')) {
    const slug = normalizedPath.replace('/tutorials/', '');
    if (slug) {
      return {
        title: `${slug.replace(/-/g, ' ')} | Layerre Video Tutorials`,
        description:
          'Step-by-step video guide for automating design generation with Layerre.',
        image: 'https://layerre.com/link-card.png',
        url: `https://layerre.com${normalizedPath}/`,
        type: 'article',
        siteName: 'Layerre',
      };
    }
  }

  // FAQ pages
  if (normalizedPath === '/faq') {
    return {
      title: 'FAQ | Layerre',
      description:
        'Answers to common questions about design automation, Canva workflows, and image generation APIs.',
      image: 'https://layerre.com/link-card.png',
      url: 'https://layerre.com/faq/',
      type: 'website',
      siteName: 'Layerre',
    };
  }
  if (normalizedPath.startsWith('/faq/')) {
    const slug = normalizedPath.replace('/faq/', '');
    if (slug) {
      return {
        title: `${slug.replace(/-/g, ' ')} | Layerre FAQ`,
        description:
          'Learn how to automate design generation with Layerre, including Canva import, APIs, and no-code integrations.',
        image: 'https://layerre.com/link-card.png',
        url: `https://layerre.com${normalizedPath}/`,
        type: 'article',
        siteName: 'Layerre',
      };
    }
  }

  // Glossary pages
  if (normalizedPath === '/glossary') {
    return {
      title: 'Glossary | Layerre',
      description:
        'Definitions and explanations of key terms in design automation and image generation APIs.',
      image: 'https://layerre.com/link-card.png',
      url: 'https://layerre.com/glossary/',
      type: 'website',
      siteName: 'Layerre',
    };
  }
  if (normalizedPath.startsWith('/glossary/')) {
    const slug = normalizedPath.replace('/glossary/', '');
    if (slug) {
      return {
        title: `${slug.replace(/-/g, ' ')} | Layerre Glossary`,
        description:
          'Clear, practical definitions of design automation concepts with examples and related terms.',
        image: 'https://layerre.com/link-card.png',
        url: `https://layerre.com${normalizedPath}/`,
        type: 'article',
        siteName: 'Layerre',
      };
    }
  }

  // Authenticated app area
  if (normalizedPath.startsWith('/app')) {
    const p = normalizedPath || '/app';
    let area = 'Dashboard';
    if (p === '/app' || p === '/app/dashboard') {
      area = 'Dashboard';
    } else if (p.startsWith('/app/api-keys')) {
      area = 'API Keys';
    } else if (p.startsWith('/app/billing')) {
      area = 'Usage & Billing';
    } else if (p.startsWith('/app/integrations')) {
      area = 'Integrations';
    } else if (p.startsWith('/app/create')) {
      area = 'Create';
    } else if (p.startsWith('/app/profile')) {
      area = 'Profile';
    }
    return {
      title: `Layerre | ${area}`,
      description:
        'Manage templates, API keys, and billing in your Layerre account.',
      image: 'https://layerre.com/link-card.png',
      url: `https://layerre.com${normalizedPath}/`,
      type: 'website',
      siteName: 'Layerre',
    };
  }

  // Default fallback
  return DEFAULT_SEO;
}
